import { useMemo, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Send, Trash2, Save, Loader2, Eye } from "lucide-react";
import ImageInput from "@/components/ImageInput";
import FlipCard from "@/components/FlipCard";

const CATEGORY_OPTIONS = ["GS-1", "GS-2", "GS-3", "GS-4", "Prelims", "PIB", "Ethics"];

/**
 * Inline draft editor. Renders one draft card with editable fields (headline,
 * summary, prelims pointers, mains utility, tags) and three actions:
 *   - Save changes  → PUT /admin/cards/{id} with the new field values
 *   - Publish       → POST /admin/cards/{id}/publish (also saves first if dirty)
 *   - Discard       → DELETE /admin/cards/{id}
 *
 * Prelims Pointers is edited as a multi-line textarea; each non-empty line
 * becomes an item. Tags are comma-separated.
 */
export default function DraftCardEditor({ card, onChanged, onDeleted, onPublished }) {
  const [headline, setHeadline] = useState(card.headline || "");
  const [summary, setSummary] = useState(card.summary_60 || "");
  const [fullBrief, setFullBrief] = useState(card.full_brief || "");
  const [pointers, setPointers] = useState((card.prelims_pointers || []).join("\n"));
  const [mains, setMains] = useState(card.mains_utility || "");
  const [tags, setTags] = useState((card.tags || []).join(", "));
  const [imageUrl, setImageUrl] = useState(card.image_url || "");
  const [diagramUrl, setDiagramUrl] = useState(card.diagram_url || "");
  const [category, setCategory] = useState(card.category || "GS-3");
  const [stateTag, setStateTag] = useState(card.state_tag || "");
  const [pibTag, setPibTag] = useState(!!card.pib_tag);
  const [dataTag, setDataTag] = useState(!!card.data_tag);
  const [ethicsTag, setEthicsTag] = useState(!!card.ethics_tag);
  const [pyqTag, setPyqTag] = useState(card.pyq_tag || "");
  const [source, setSource] = useState(card.source || "");
  const [sourceUrl, setSourceUrl] = useState(card.source_url || "");
  const [busy, setBusy] = useState(null); // 'save' | 'publish' | 'discard' | null

  const wordCount = summary.trim().split(/\s+/).filter(Boolean).length;
  const briefWordCount = fullBrief.trim().split(/\s+/).filter(Boolean).length;
  // Summary "sweet spot" is 60-90 words (fits comfortably on the card front).
  // 20-59 or 91-120 is acceptable but flagged amber. Anything outside 20-120 is rejected.
  const summaryTone =
    wordCount < 20 || wordCount > 120
      ? "text-[#B42318] font-normal"
      : wordCount >= 60 && wordCount <= 90
        ? "text-[#7DE3B6] font-normal"
        : "text-[#F2BD68] font-normal";

  const dirty =
    headline !== (card.headline || "") ||
    summary !== (card.summary_60 || "") ||
    fullBrief !== (card.full_brief || "") ||
    pointers !== (card.prelims_pointers || []).join("\n") ||
    mains !== (card.mains_utility || "") ||
    tags !== (card.tags || []).join(", ") ||
    imageUrl !== (card.image_url || "") ||
    diagramUrl !== (card.diagram_url || "") ||
    category !== (card.category || "GS-3") ||
    stateTag !== (card.state_tag || "") ||
    pibTag !== !!card.pib_tag ||
    dataTag !== !!card.data_tag ||
    ethicsTag !== !!card.ethics_tag ||
    pyqTag !== (card.pyq_tag || "") ||
    source !== (card.source || "") ||
    sourceUrl !== (card.source_url || "");

  const buildPayload = () => ({
    ...card,
    headline: headline.trim(),
    summary_60: summary.trim(),
    full_brief: fullBrief.trim() || null,
    prelims_pointers: pointers.split("\n").map(s => s.trim()).filter(Boolean),
    mains_utility: mains.trim(),
    tags: tags.split(",").map(s => s.trim()).filter(Boolean),
    image_url: imageUrl.trim(),
    diagram_url: diagramUrl?.trim() || null,
    category,
    state_tag: stateTag.trim() || null,
    pib_tag: !!pibTag,
    data_tag: !!dataTag,
    ethics_tag: !!ethicsTag,
    pyq_tag: pyqTag.trim() || null,
    source: source.trim(),
    source_url: sourceUrl.trim() || null,
  });

  const validate = () => {
    if (!headline.trim()) return "Headline is required.";
    if (!summary.trim()) return "Summary is required.";
    if (!imageUrl.trim()) return "Feature Image is required. Paste a URL or upload one.";
    if (wordCount < 20) return "Summary must be at least 20 words.";
    if (wordCount > 120) return "Summary must be under 120 words.";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy("save");
    try {
      const updated = await adminApi.updateCard(card.card_id, buildPayload());
      toast.success("Draft saved.");
      onChanged && onChanged(updated);
    } catch (e) {
      console.error("[drafts] Save failed:", e);
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(null); }
  };

  const publish = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy("publish");
    try {
      // Save any pending inline edits first so publish reflects the latest content.
      if (dirty) await adminApi.updateCard(card.card_id, buildPayload());
      const r = await adminApi.publishCard(card.card_id);
      toast.success("Published — now live on the public feed.");
      onPublished && onPublished(r.card);
    } catch (e) {
      console.error("[drafts] Publish failed:", e);
      toast.error(e?.response?.data?.detail || "Publish failed.");
    } finally { setBusy(null); }
  };

  const discard = async () => {
    if (!window.confirm("Discard this draft permanently? This cannot be undone.")) return;
    setBusy("discard");
    try {
      await adminApi.deleteCard(card.card_id);
      toast.success("Draft discarded.");
      onDeleted && onDeleted(card.card_id);
    } catch (e) {
      console.error("[drafts] Discard failed:", e);
      toast.error(e?.response?.data?.detail || "Discard failed.");
    } finally { setBusy(null); }
  };

  const busyPublish = busy === "publish";
  const busyDiscard = busy === "discard";
  const busySave = busy === "save";
  const anyBusy = !!busy;

  // Live preview card — recomputes on every keystroke so the reader can see
  // exactly how the draft will render in the public feed before publishing.
  // A trailing "…" fills empty required fields so the preview never crashes.
  const livePreviewCard = useMemo(() => ({
    card_id: card.card_id,
    headline: headline.trim() || "Untitled draft — headline pending",
    summary_60: summary.trim() || "Add a 60–90 word summary to fill this space. The reader sees this on the card front.",
    full_brief: fullBrief.trim() || null,
    category: category || "GS-3",
    state_tag: stateTag.trim() || null,
    pib_tag: !!pibTag,
    data_tag: !!dataTag,
    ethics_tag: !!ethicsTag,
    pyq_tag: pyqTag.trim() || null,
    prelims_pointers: pointers.split("\n").map(s => s.trim()).filter(Boolean),
    mains_utility: mains.trim(),
    tags: tags.split(",").map(s => s.trim()).filter(Boolean),
    image_url: imageUrl.trim() || card.image_url || "",
    diagram_url: diagramUrl?.trim() || null,
    source: source.trim(),
    source_url: sourceUrl.trim() || null,
    published_at: card.published_at || new Date().toISOString(),
    is_revision: false,
    is_weak_spot: false,
  }), [
    card.card_id, card.image_url, card.published_at,
    headline, summary, fullBrief, category, stateTag,
    pibTag, dataTag, ethicsTag, pyqTag,
    pointers, mains, tags, imageUrl, diagramUrl, source, sourceUrl,
  ]);

  return (
    <div
      className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-4 sm:p-5 shadow-[0_8px_24px_rgba(0,0,0,0.20)]"
      data-testid="draft-card"
      data-card-id={card.card_id}
    >
      <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,400px)] xl:gap-6">
        <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center rounded-full bg-[#F2BD68]/10 border border-[#F2BD68]/30 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#F2BD68]">
          Draft
        </span>
        <span className="text-[11px] text-[#94A3B8]">{category}{stateTag ? ` · ${stateTag}` : ""}</span>
        <span className="text-[11px] text-[#64748B]">· {card.card_id}</span>
      </div>

      {/* Classification block — Category, State tag, Tag flags, PYQ, Source */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={anyBusy}
            data-testid={`draft-category-${card.card_id}`}
            className="mt-1.5 h-10 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
          >
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
            State tag <span className="text-[#64748B] font-normal">· optional</span>
          </label>
          <input
            value={stateTag}
            onChange={(e) => setStateTag(e.target.value)}
            disabled={anyBusy}
            placeholder="e.g. HPSC, MPSC"
            data-testid={`draft-state-tag-${card.card_id}`}
            className="mt-1.5 h-10 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
          />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold cursor-pointer transition-colors ${pibTag ? "border-[#E85A2A] bg-[#E85A2A]/10 text-[#F8FAFC]" : "border-[#26334D] bg-[#111622] text-[#94A3B8] hover:bg-[#232E45]"}`}>
          <input
            type="checkbox"
            checked={pibTag}
            onChange={(e) => setPibTag(e.target.checked)}
            disabled={anyBusy}
            data-testid={`draft-pib-${card.card_id}`}
            className="accent-[#E85A2A]"
          />
          PIB Filtered
        </label>
        <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold cursor-pointer transition-colors ${dataTag ? "border-[#E85A2A] bg-[#E85A2A]/10 text-[#F8FAFC]" : "border-[#26334D] bg-[#111622] text-[#94A3B8] hover:bg-[#232E45]"}`}>
          <input
            type="checkbox"
            checked={dataTag}
            onChange={(e) => setDataTag(e.target.checked)}
            disabled={anyBusy}
            data-testid={`draft-data-${card.card_id}`}
            className="accent-[#E85A2A]"
          />
          Data (stats/reports)
        </label>
        <label className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold cursor-pointer transition-colors ${ethicsTag ? "border-[#E85A2A] bg-[#E85A2A]/10 text-[#F8FAFC]" : "border-[#26334D] bg-[#111622] text-[#94A3B8] hover:bg-[#232E45]"}`}>
          <input
            type="checkbox"
            checked={ethicsTag}
            onChange={(e) => setEthicsTag(e.target.checked)}
            disabled={anyBusy}
            data-testid={`draft-ethics-${card.card_id}`}
            className="accent-[#E85A2A]"
          />
          Ethics &amp; Essay
        </label>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
            PYQ tag <span className="text-[#64748B] font-normal">· optional</span>
          </label>
          <input
            value={pyqTag}
            onChange={(e) => setPyqTag(e.target.value)}
            disabled={anyBusy}
            placeholder="e.g. Prelims 2019"
            data-testid={`draft-pyq-${card.card_id}`}
            className="mt-1.5 h-10 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
          />
        </div>
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Source</label>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            disabled={anyBusy}
            placeholder="PIB, The Hindu, PRS…"
            data-testid={`draft-source-${card.card_id}`}
            className="mt-1.5 h-10 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
          />
        </div>
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
            Source URL <span className="text-[#64748B] font-normal">· optional</span>
          </label>
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            disabled={anyBusy}
            placeholder="https://…"
            data-testid={`draft-source-url-${card.card_id}`}
            className="mt-1.5 h-10 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
          />
        </div>
      </div>

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Headline</label>
      <input
        value={headline}
        onChange={(e) => setHeadline(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-headline"
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2.5 text-sm font-semibold text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
      />

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
        Summary <span className={summaryTone}>· {wordCount} words</span>
        <span className="ml-2 text-[#64748B] font-normal normal-case tracking-normal">sweet spot: 60–90</span>
      </label>
      <p className="mt-1 text-[11px] text-[#64748B] leading-relaxed">
        Line breaks and bullets (<span className="text-[#F2BD68]">•</span>, <span className="text-[#F2BD68]">-</span>, <span className="text-[#F2BD68]">→</span>) render on the card.
        Wrap words in <span className="text-[#F2BD68]">*italic*</span>, <span className="text-[#F2BD68]">**bold**</span>, or <span className="text-[#F2BD68]">***both***</span> for emphasis.
      </p>
      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-summary"
        rows={6}
        placeholder={"60–90 word overview that fits the card front.\n\nExample:\n• Point one\n• Point two"}
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2.5 text-sm text-[#E2E8F0] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B] whitespace-pre-wrap"
      />

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
        Full brief <span className="text-[#64748B] font-normal">· optional</span>
        {fullBrief && (
          <span className="ml-2 text-[#94A3B8] font-normal normal-case tracking-normal">{briefWordCount} words</span>
        )}
      </label>
      <p className="mt-1 text-[11px] text-[#64748B] leading-relaxed">
        Longer explainer shown when the reader taps <span className="text-[#F2BD68]">Flip</span>. Use this for background, definitions, or context that didn't fit in the summary.
      </p>
      <textarea
        value={fullBrief}
        onChange={(e) => setFullBrief(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-full-brief"
        rows={6}
        placeholder="Expand the story here. Multiple paragraphs are fine — line breaks are preserved on the card back."
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2.5 text-sm text-[#E2E8F0] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
      />

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
        Prelims pointers <span className="text-[#64748B] font-normal">· one per line</span>
      </label>
      <textarea
        value={pointers}
        onChange={(e) => setPointers(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-pointers"
        rows={3}
        placeholder="Article 123&#10;Set up in 1985&#10;Reports to the President"
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2.5 text-sm text-[#E2E8F0] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E85A2A] font-mono placeholder-[#64748B]"
      />

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Mains utility</label>
      <textarea
        value={mains}
        onChange={(e) => setMains(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-mains"
        rows={2}
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2.5 text-sm text-[#E2E8F0] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
      />

      <label className="mt-4 block text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">
        Tags <span className="text-[#64748B] font-normal">· comma separated</span>
      </label>
      <input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        disabled={anyBusy}
        data-testid="draft-tags"
        placeholder="Polity, Judiciary, Article 123"
        className="mt-1.5 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 py-2 text-sm text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#E85A2A] placeholder-[#64748B]"
      />

      <div className="mt-4">
        <ImageInput
          label="Feature Image"
          value={imageUrl}
          onChange={setImageUrl}
          aspect={16 / 9}
          helper="The big image on the card front. Paste a URL, or upload from your device and frame the 16:9 crop."
          testId={`draft-feature-image-${card.card_id}`}
        />
      </div>

      <div className="mt-4">
        <ImageInput
          label="Diagram Image (optional)"
          value={diagramUrl}
          onChange={(v) => setDiagramUrl(v || "")}
          helper="Optional. Shows on the card back — original aspect ratio preserved. Ideal for maps, charts, or flow diagrams."
          testId={`draft-diagram-image-${card.card_id}`}
        />
      </div>
        </div>{/* /left column (editor fields) */}

        {/* Live preview column — sticky at xl breakpoint; renders inline on smaller screens */}
        <aside className="mt-6 xl:mt-0" data-testid="draft-live-preview">
          <div className="xl:sticky xl:top-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-3.5 w-3.5 text-[#F2BD68]" aria-hidden />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#F2BD68]">Live Preview</span>
              <span className="text-[10.5px] text-[#64748B]">· how this looks on the feed</span>
            </div>
            <div
              className="rounded-2xl bg-[#111622] border border-[#26334D] p-3 sm:p-4"
              style={{ perspective: 1200 }}
            >
              {/* Fixed-aspect frame that mirrors the mobile feed viewport (16:9-ish). */}
              <div
                className="relative w-full mx-auto"
                style={{ maxWidth: 360, height: 560 }}
              >
                <FlipCard
                  card={livePreviewCard}
                  isBookmarked={false}
                  onToggleBookmark={() => toast("Bookmarks are disabled in the preview.")}
                />
              </div>
            </div>
            <p className="mt-2 text-[10.5px] text-[#64748B] leading-relaxed">
              Tap <span className="text-[#F2BD68]">Flip</span> in the preview to inspect the back — pointers, diagram, mains utility, and full brief.
            </p>
          </div>
        </aside>
      </div>{/* /2-col grid */}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={publish}
          disabled={anyBusy}
          data-testid="draft-publish"
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#2F6F4F] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#256040] active:scale-[0.98] disabled:opacity-50 transition-all shadow-[0_6px_18px_rgba(47,111,79,0.35)]"
        >
          {busyPublish ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {busyPublish ? "Publishing…" : "Publish now"}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={anyBusy || !dirty}
          data-testid="draft-save"
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#1A2232] border border-[#26334D] px-3.5 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] disabled:opacity-40 transition-colors"
        >
          {busySave ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {busySave ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
        <div className="grow" />
        <button
          type="button"
          onClick={discard}
          disabled={anyBusy}
          data-testid="draft-discard"
          className="inline-flex items-center gap-1.5 rounded-xl border border-[#26334D] bg-[#1A2232] px-3.5 py-2.5 text-sm font-semibold text-[#B42318] hover:bg-[#B42318]/10 disabled:opacity-50 transition-colors"
        >
          {busyDiscard ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Discard
        </button>
      </div>
    </div>
  );
}
